import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Space,
  Spin,
  message,
  Table,
  Modal,
  Alert,
  Progress,
  Typography,
  Descriptions,
  Empty,
  InputNumber
} from 'antd';
import {
  WalletOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  CreditCardOutlined,
  BankOutlined,
  StarFilled,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/admin/governanceService';
import ListPageHeader from '@shared/ListPageHeader';
import { formatDateTime } from '@utils/dateTime';

const { Text } = Typography;

const Overview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [budgetModalVisible, setBudgetModalVisible] = useState(false);
  const [budgetValue, setBudgetValue] = useState(null);
  const [budgetUnlimited, setBudgetUnlimited] = useState(true);
  const [updatingBudget, setUpdatingBudget] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [walletRes, summaryRes, packsRes, historyRes] = await Promise.all([
        governanceService.getAiWallet(),
        governanceService.getUsageSummary(),
        governanceService.getAiCreditPacks(),
        governanceService.getAiCreditPurchaseHistory()
      ]);

      if (walletRes.success) setWallet(walletRes.data.ai_wallet || walletRes.data);
      if (summaryRes.success) setSummary(summaryRes.data.summary || summaryRes.data);
      if (packsRes.success) setCreditPacks(packsRes.data.packs || []);
      if (historyRes.success) setPurchaseHistory(historyRes.data.purchases || []);
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePurchaseClick = async (pack) => {
    setSelectedPack(pack);
    setPreviewing(true);
    setPurchaseModalVisible(true);

    try {
      const response = await governanceService.purchaseAiCredits(pack.id, false);
      if (response.success) {
        setPreviewData(response.data);
      } else {
        message.error(response.message || t('admin.ai_center.overview.purchase_failed'));
        setPurchaseModalVisible(false);
      }
    } catch (error) {
      console.error('Failed to preview purchase:', error);
      message.error(t('admin.ai_center.overview.purchase_failed'));
      setPurchaseModalVisible(false);
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!selectedPack) return;

    try {
      setPurchasing(true);
      const response = await governanceService.purchaseAiCredits(selectedPack.id, true);

      if (response.success) {
        message.success(t('admin.ai_center.overview.purchase_success'));
        setPurchaseModalVisible(false);
        setSelectedPack(null);
        setPreviewData(null);
        fetchData();
      } else {
        message.error(response.message || t('admin.ai_center.overview.purchase_failed'));
      }
    } catch (error) {
      console.error('Failed to purchase:', error);
      message.error(t('admin.ai_center.overview.purchase_failed'));
    } finally {
      setPurchasing(false);
    }
  };

  const handleClosePurchaseModal = () => {
    setPurchaseModalVisible(false);
    setSelectedPack(null);
    setPreviewData(null);
  };

  const handleOpenBudgetModal = () => {
    const isUnlimited = wallet?.daily_budget_unlimited;
    setBudgetUnlimited(isUnlimited);
    setBudgetValue(isUnlimited ? null : wallet?.daily_budget_limit);
    setBudgetModalVisible(true);
  };

  const handleSaveBudget = async () => {
    const limit = budgetUnlimited ? -1 : (budgetValue || 0);
    try {
      setUpdatingBudget(true);
      const response = await governanceService.updateDailyBudget(limit);
      if (response.success) {
        message.success(t('admin.ai_center.overview.budget_updated'));
        setBudgetModalVisible(false);
        fetchData();
      } else {
        message.error(response.message || t('common.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to update daily budget:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setUpdatingBudget(false);
    }
  };

  const historyColumns = [
    {
      title: t('admin.ai_center.overview.purchase_date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDateTime(date)
    },
    {
      title: t('admin.ai_center.overview.purchase_pack'),
      dataIndex: 'pack_name',
      key: 'pack_name'
    },
    {
      title: t('admin.ai_center.overview.purchase_amount'),
      dataIndex: 'amount_paid',
      key: 'amount_paid',
      align: 'right',
      render: (amount) => (
        <Text strong>{currencySymbol} {amount?.toLocaleString()}</Text>
      )
    },
    {
      title: t('admin.ai_center.overview.purchase_status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = { completed: 'success', pending: 'processing', failed: 'error', cancelled: 'default' };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const currencySymbol = wallet?.currency === 'TWD' ? 'NT$' : '$';

  const usagePercent = wallet?.lifetime_purchased > 0
    ? Math.round((wallet.lifetime_consumed / wallet.lifetime_purchased) * 100)
    : 0;

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader items={['ai_center', 'ai_center.overview']} basePath="/admin" />

      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Setup Alerts */}
          {wallet && !wallet.has_billing_profile && (
            <Alert
              message={t('admin.ai_center.overview.setup_billing_alert')}
              type="warning"
              showIcon
              action={
                <Button size="small" onClick={() => navigate('/ai_center/billing_profile')}>
                  {t('admin.ai_center.overview.setup_billing_button')}
                </Button>
              }
            />
          )}

          {wallet && !wallet.has_payment_method && (
            <Alert
              message={t('admin.ai_center.overview.setup_payment_alert')}
              type="warning"
              showIcon
              action={
                <Button size="small" onClick={() => navigate('/ai_center/payment_methods')}>
                  {t('admin.ai_center.overview.setup_payment_button')}
                </Button>
              }
            />
          )}

          {/* Wallet Status */}
          <Card
            title={
              <Space>
                <WalletOutlined />
                {t('admin.ai_center.overview.wallet_title')}
              </Space>
            }
            extra={
              <Tag color={wallet?.can_proceed ? 'green' : 'red'}>
                {wallet?.can_proceed
                  ? t('admin.ai_center.overview.status_active')
                  : t('admin.ai_center.overview.status_inactive')}
              </Tag>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card bordered={false}>
                  <Statistic
                    title={t('admin.ai_center.overview.balance')}
                    value={wallet?.balance ?? 0}
                    precision={2}
                    prefix={currencySymbol}
                    valueStyle={{ color: wallet?.balance > 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card bordered={false} style={{ cursor: 'pointer' }} onClick={handleOpenBudgetModal}>
                  <Statistic
                    title={
                      <Space>
                        {t('admin.ai_center.overview.daily_budget')}
                        <EditOutlined style={{ fontSize: 12, color: '#1890ff' }} />
                      </Space>
                    }
                    value={wallet?.daily_budget_unlimited ? t('admin.ai_center.overview.unlimited') : (wallet?.daily_budget_limit ?? '-')}
                    prefix={wallet?.daily_budget_unlimited ? undefined : currencySymbol}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card bordered={false}>
                  <Statistic
                    title={t('admin.ai_center.overview.daily_remaining')}
                    value={wallet?.daily_budget_unlimited ? t('admin.ai_center.overview.unlimited') : (wallet?.daily_budget_remaining ?? '-')}
                    prefix={wallet?.daily_budget_unlimited ? undefined : currencySymbol}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card bordered={false}>
                  <Statistic
                    title={t('admin.ai_center.overview.daily_consumed')}
                    value={wallet?.today_consumed ?? 0}
                    prefix={currencySymbol}
                  />
                </Card>
              </Col>
            </Row>

            {wallet?.lifetime_purchased > 0 && (
              <div style={{ marginTop: 24 }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={12} lg={6}>
                    <Statistic
                      title={t('admin.ai_center.overview.total_purchased')}
                      value={wallet.lifetime_purchased}
                      prefix={currencySymbol}
                      precision={2}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Statistic
                      title={t('admin.ai_center.overview.total_consumed')}
                      value={wallet.lifetime_consumed ?? 0}
                      prefix={currencySymbol}
                      precision={2}
                    />
                  </Col>
                  <Col xs={24} sm={24} lg={12}>
                    <Text type="secondary">{t('admin.ai_center.overview.usage_ratio')}</Text>
                    <Progress percent={usagePercent} status={usagePercent > 90 ? 'exception' : 'active'} />
                  </Col>
                </Row>
              </div>
            )}
          </Card>

          {/* Credit Packs */}
          <Card
            title={
              <Space>
                <ShoppingCartOutlined />
                {t('admin.ai_center.overview.credit_packs_title')}
              </Space>
            }
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('admin.ai_center.overview.credit_packs_desc')}
            </Text>
            {creditPacks.length > 0 ? (
              <div>
                {creditPacks.map((pack, index) => {
                  const isRecommended = index === Math.floor(creditPacks.length / 2);
                  const isBestValue = index === creditPacks.length - 1 && pack.bonus_percentage > 0;

                  return (
                    <div
                      key={pack.id}
                      style={{
                        padding: '16px 20px',
                        border: isRecommended ? '2px solid #1890ff' : '1px solid #f0f0f0',
                        borderRadius: 8,
                        marginBottom: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: isRecommended ? '#f0f7ff' : '#fff',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      onClick={() => handlePurchaseClick(pack)}
                      onMouseEnter={(e) => {
                        if (!isRecommended) e.currentTarget.style.borderColor = '#91caff';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isRecommended) e.currentTarget.style.borderColor = '#f0f0f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong style={{ fontSize: 20 }}>{pack.name}</Text>
                        {isRecommended && (
                          <Tag color="blue" icon={<StarFilled />}>
                            {t('admin.ai_center.overview.pack_recommended')}
                          </Tag>
                        )}
                        {isBestValue && !isRecommended && (
                          <Tag color="gold">
                            {t('admin.ai_center.overview.pack_best_value')}
                          </Tag>
                        )}
                        {pack.bonus_percentage > 0 && (
                          <Tag color="green">+{pack.bonus_percentage}% {t('admin.ai_center.overview.pack_bonus')}</Tag>
                        )}
                      </div>
                      <Button
                        type={isRecommended ? 'primary' : 'default'}
                        icon={<ThunderboltOutlined />}
                        onClick={(e) => { e.stopPropagation(); handlePurchaseClick(pack); }}
                      >
                        {t('admin.ai_center.overview.purchase')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty description={t('admin.ai_center.overview.no_purchase_history')} />
            )}
          </Card>

          {/* Purchase History */}
          <Card title={t('admin.ai_center.overview.purchase_history_title')}>
            {purchaseHistory.length > 0 ? (
              <Table
                dataSource={purchaseHistory}
                columns={historyColumns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
              />
            ) : (
              <Empty description={t('admin.ai_center.overview.no_purchase_history')} />
            )}
          </Card>
        </Space>
      </div>

      {/* Purchase Preview Modal */}
      <Modal
        title={t('admin.ai_center.overview.purchase_preview_title')}
        open={purchaseModalVisible}
        onCancel={handleClosePurchaseModal}
        onOk={handleConfirmPurchase}
        confirmLoading={purchasing}
        okText={t('admin.ai_center.overview.confirm_purchase')}
        cancelText={t('common.button.cancel')}
      >
        {previewing ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : previewData ? (
          <>
            <Alert
              message={t('admin.ai_center.overview.purchase_preview_desc')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t('admin.ai_center.overview.pack_name')}>
                {previewData.pack_name || selectedPack?.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.ai_center.overview.pack_credits')}>
                {currencySymbol} {(previewData.credits || selectedPack?.credits)?.toLocaleString()}
              </Descriptions.Item>
              {(previewData.bonus_credits > 0 || previewData.bonus > 0) && (
                <Descriptions.Item label={t('admin.ai_center.overview.pack_bonus')}>
                  <Text style={{ color: '#52c41a' }}>
                    + {currencySymbol} {(previewData.bonus_credits || previewData.bonus)?.toLocaleString()}
                  </Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('admin.ai_center.overview.pack_subtotal')}>
                {currencySymbol} {(previewData.subtotal || previewData.amount || selectedPack?.price)?.toLocaleString()}
              </Descriptions.Item>
              {previewData.tax > 0 && (
                <Descriptions.Item label={t('admin.ai_center.overview.pack_tax', { rate: (previewData.tax_rate * 100).toFixed(0) })}>
                  {currencySymbol} {previewData.tax?.toLocaleString()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('admin.ai_center.overview.pack_price')}>
                <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                  {currencySymbol} {(previewData.total || previewData.amount || selectedPack?.price)?.toLocaleString()}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.ai_center.overview.payment_method')}>
                {previewData.payment_method === 'bank_transfer' ? (
                  <Space>
                    <BankOutlined />
                    {t('admin.ai_center.overview.payment_method_bank_transfer')}
                  </Space>
                ) : (
                  <Space>
                    <CreditCardOutlined />
                    {t('admin.ai_center.overview.payment_method_credit_card')}
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : null}
      </Modal>

      {/* Daily Budget Modal */}
      <Modal
        title={t('admin.ai_center.overview.edit_daily_budget')}
        open={budgetModalVisible}
        onCancel={() => setBudgetModalVisible(false)}
        onOk={handleSaveBudget}
        confirmLoading={updatingBudget}
        okText={t('common.button.save')}
        cancelText={t('common.button.cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{t('admin.ai_center.overview.daily_budget_desc')}</Text>
        </div>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Button
              type={budgetUnlimited ? 'primary' : 'default'}
              onClick={() => { setBudgetUnlimited(true); setBudgetValue(null); }}
              style={{ marginRight: 8 }}
            >
              {t('admin.ai_center.overview.unlimited')}
            </Button>
            <Button
              type={!budgetUnlimited ? 'primary' : 'default'}
              onClick={() => { setBudgetUnlimited(false); setBudgetValue(wallet?.daily_budget_limit > 0 ? wallet.daily_budget_limit : 1000); }}
            >
              {t('admin.ai_center.overview.custom_limit')}
            </Button>
          </div>
          {!budgetUnlimited && (
            <InputNumber
              value={budgetValue}
              onChange={setBudgetValue}
              min={1}
              style={{ width: '100%' }}
              prefix={currencySymbol}
              placeholder={t('admin.ai_center.overview.enter_daily_limit')}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default Overview;
